/**
 * Optimized HomeProduct Component
 * Performance improvements:
 * - React.memo for preventing unnecessary re-renders
 * - useMemo for static config
 * - useCallback for event handlers
 * - Intersection Observer for view tracking
 * - Analytics tracking for views and CTA clicks
 * - Next.js Image component for optimized image loading
 */

'use client';

import { memo, useMemo, useCallback, useRef, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { productConfig } from "@/constant/home";
import { useIntersectionObserver, useAnalytics } from '@/hooks/usePerformance';

function HomeProductComponent() {
    const sectionRef = useRef<HTMLDivElement>(null);
    const hasTrackedView = useRef(false);
    const { trackEvent } = useAnalytics();

    // Memoize config to prevent re-creation
    const config = useMemo(() => productConfig, []);

    // Track section visibility
    const isInView = useIntersectionObserver(sectionRef, {
        threshold: 0.3,
        rootMargin: '50px',
    });

    // Track when section becomes visible (only once)
    useEffect(() => {
        if (isInView && !hasTrackedView.current) {
            trackEvent('product_section_view', {
                section_label: config.sectionLabel,
                section_heading: config.heading,
            });
            hasTrackedView.current = true;
        }
    }, [isInView, config.sectionLabel, config.heading, trackEvent]);

    // Track CTA clicks
    const handleCTAClick = useCallback((ctaText: string, ctaHref: string, ctaIndex: number) => {
        trackEvent('product_cta_clicked', {
            cta_text: ctaText,
            cta_destination: ctaHref,
            cta_index: ctaIndex,
            section: 'product',
        });
    }, [trackEvent]);

    return (
        <div ref={sectionRef} className="section_home-product">
            <div className="padding-global">
                <div className="container-large">
                    <div className="padding-top padding-xlarge">
                        <div className="features_component">
                            <div className="features_image-wrapper background-color-pink-50">
                                <Image
                                    src={config.image}
                                    alt={config.sectionLabel}
                                    width={800}
                                    height={600}
                                    className="apps_image rounded-lg"
                                    loading="lazy"
                                    priority={false}
                                />
                            </div>
                            <div className="features_content-wrapper max-width-small">
                                <div className="text-style-allcaps text-color-gray-50 dark:text-gray-300">
                                    {config.sectionLabel}
                                </div>
                                <div className="padding-bottom padding-xsmall"></div>
                                <h2 className="text-[var(--primary--burgundy)] dark:text-[var(--primary--pink)]">
                                    {config.heading}
                                </h2>
                                <div className="padding-bottom padding-xsmall"></div>
                                {config.paragraphs.map((p, i) => (
                                    <p
                                        key={`product-paragraph-${i}`}
                                        className="text-color-gray-50 dark:text-gray-300"
                                        dangerouslySetInnerHTML={{ __html: p }}
                                    />
                                ))}
                                <div className="padding-bottom padding-small"></div>
                                <div className="button-row">
                                    {config.ctas.map((cta, i) => (
                                        <Link
                                            href={cta.href}
                                            className={cta.className}
                                            key={`product-cta-${i}`}
                                            onClick={() => handleCTAClick(cta.text, cta.href, i)}
                                        >
                                            {cta.text}
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="padding-bottom padding-xhuge"></div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Export memoized component
export const HomeProduct = memo(HomeProductComponent);
HomeProduct.displayName = 'HomeProduct';

// Default export for backward compatibility
export default HomeProduct;
